import { useContactInvalidation } from "@/hooks/use-contact-invalidation";
import {
  createContactPhoneCallV2,
  deleteContactPhoneCallV2,
  retrieveContactPhoneCallsV2,
  updateContactPhoneCallV2,
  type RetrieveContactPhoneCallsParams,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { APIError } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import {
  infiniteQueryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import toast from "react-hot-toast";
import type { ContactSubitemMutationOptions } from "./contact-subitem-mutation-options";

export const contactPhoneCallsQueryKey = (contactId?: string) =>
  [queryKeys.contactPhoneCalls, contactId ?? "all"] as const;

export const retrieveContactPhoneCallsInfiniteQueryOptions = ({
  contactId,
  limit = 50,
}: Pick<RetrieveContactPhoneCallsParams, "contactId" | "limit"> = {}) =>
  infiniteQueryOptions({
    queryKey: [...contactPhoneCallsQueryKey(contactId), { limit }],
    enabled: contactId !== "",
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      retrieveContactPhoneCallsV2({
        contactId,
        cursor: pageParam ?? undefined,
        limit,
        signal,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.pagination?.nextCursor ?? undefined,
  });

const useInvalidateContactPhoneCalls = () => {
  const queryClient = useQueryClient();
  const invalidateContact = useContactInvalidation();

  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [queryKeys.contactPhoneCalls],
      }),
      invalidateContact(),
    ]);
  };
};

export const useCreateContactPhoneCall = (
  options?: ContactSubitemMutationOptions,
) => {
  const invalidate = useInvalidateContactPhoneCalls();

  return useMutation({
    mutationFn: createContactPhoneCallV2,
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error recording phone call");
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Phone call recorded successfully");
      }
    },
    onSettled: invalidate,
  });
};

export const useUpdateContactPhoneCall = (
  options?: ContactSubitemMutationOptions,
) => {
  const invalidate = useInvalidateContactPhoneCalls();

  return useMutation({
    mutationFn: updateContactPhoneCallV2,
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating phone call");
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Phone call updated successfully");
      }
    },
    onSettled: invalidate,
  });
};

export const useDeleteContactPhoneCall = (
  options?: ContactSubitemMutationOptions,
) => {
  const invalidate = useInvalidateContactPhoneCalls();

  return useMutation({
    mutationFn: deleteContactPhoneCallV2,
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error deleting phone call");
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Phone call deleted successfully");
      }
    },
    onSettled: invalidate,
  });
};
